import React from 'react';
import { useTranslation } from 'react-i18next';
import uuid from 'uuid-random';
import { sendCustomTrackingInfo } from '@panel/commons';
import {
  InputContainter,
  DataContainer,
  TitleContainer,
  SubtitleContainer,
  DataSpan,
  PContainer,
  LinkUpdate,
  WhatsAppHelp,
  WhatsAppHelpStrong,
  PhoneContainer,
  FeddbackIcon,
  FeedbackContainer
} from './styles';

const InputDataContainer = ({ postingContactData, userInfo }) => {
  const { t } = useTranslation();

  /**
   * La función valida el tipo de fuente (source) para redireccionar al link que corresponda
   * @param {String} source
   * @returns Retorna link que redirecciona a actualizar dato de contacto.
   */
  const getLinkUpdateData = source => {
    // Caso en que los datos sean de la inmobiliaria:
    if (source === 'PUBLISHER') return `${window.location.origin}/configuracion/datos`;
    // Caso en que los datos sean de la sucursal:
    if (source === 'BRANCH')
      return `${window.location.origin}/configuracion/administrarSucursales.bum?idSucursal=${userInfo.publisherId}`;
    // Caso en que los datos sean del usuario:
    if (source === 'PUBLISHER_USER') return `${window.location.origin}/configuracion/adminUsuarios.bum`;
    return null;
  };

  /**
   * La función valida el boolean entrante para devolver el string que indica el tipo de input
   * @param {Boolean} isValid
   * @returns Retorna un string de que feedback corresponde.
   */
  const getInputStatusType = isValid => (isValid ? 'positive' : 'negative');

  /**
   * La función recorre la lista entrante para devolver un boolean dependiendo si todos los telefonos son validos
   * @param {Array} acceptedInputTypes - Lista de string que hacen referencia a uno o varios tipos de telefono
   * @param {Array} phones - Lista de telefonos a validar
   * @returns {Boolean} Retorna true si todos los telefonos son validos.
   */
  const allPhonesAreValid = (acceptedInputTypes, phones) => {
    const filteredPhones = phones.filter(p => acceptedInputTypes.includes(p.phone_type));
    return filteredPhones.every(p => p.valid);
  };

  /**
   * La función recorre la lista entrante para devolver un boolean dependiendo si hay o no un telefono invalido
   * @param {Array} acceptedInputTypes - Lista de string que hacen referencia a uno o varios tipos de telefono
   * @param {Array} phones - Lista de telefonos a validar
   * @returns {Boolean} Retorna true si hay algun telefono invalido.
   */
  const hasSomePhoneInvalid = (acceptedInputTypes, phones) => {
    const filteredPhones = phones.filter(p => acceptedInputTypes.includes(p.phone_type));
    return filteredPhones.some(p => !p.valid);
  };

  return (
    <>
      <TitleContainer>{t('panel.contactosPermisos.ContactData.InputDataContainer.title')}</TitleContainer>
      {postingContactData.phone_source === 'POSTING' && postingContactData.user_responsible_name && (
        <>
          <SubtitleContainer data-panel={`subtitletitle-name-${postingContactData.phone_source}`}>
            {t('panel.contactosPermisos.ContactData.InputDataContainer.name')}
          </SubtitleContainer>
          <InputContainter data-panel={`inputContainer-name-${postingContactData.phone_source}`}>
            <DataContainer>
              <DataSpan key={uuid()}>{postingContactData.user_responsible_name}</DataSpan>
            </DataContainer>
          </InputContainter>
        </>
      )}
      {postingContactData.phone && (
        <PhoneContainer key={uuid()} data-panel='subtitletitle-phone-container'>
          <SubtitleContainer key={uuid()}>
            {t(`panel.contactosPermisos.ContactData.InputDataContainer.PHONE`)}
          </SubtitleContainer>
          <FeedbackContainer>
            <InputContainter
              key={uuid()}
              data-panel='inputContainer-phone-PHONE'
              type={getInputStatusType(allPhonesAreValid(['PHONE', 'CELL_PHONE'], postingContactData.phone))}
            >
              <DataContainer key={uuid()}>
                {postingContactData.phone.map(contactData => {
                  if (contactData.phone_type !== 'WHATSAPP')
                    return <DataSpan key={uuid()}>{contactData.phone}</DataSpan>;
                  return null;
                })}
              </DataContainer>
            </InputContainter>
            <FeddbackIcon
              type={getInputStatusType(allPhonesAreValid(['PHONE', 'CELL_PHONE'], postingContactData.phone))}
            />
          </FeedbackContainer>
        </PhoneContainer>
      )}
      {postingContactData.phone_source && postingContactData.phone_source !== 'POSTING' && (
        <>
          <PContainer
            data-panel={`inputContainer-linkContainer-${postingContactData.phone_source}`}
            hasError={hasSomePhoneInvalid(['PHONE', 'CELL_PHONE'], postingContactData.phone)}
          >
            {hasSomePhoneInvalid(['PHONE', 'CELL_PHONE'], postingContactData.phone) &&
              t('panel.contactosPermisos.ContactData.feedback.error')}
            {t(`panel.contactosPermisos.ContactData.${postingContactData.phone_source}`, {
              realEstateName:
                postingContactData.phone_source === 'PUBLISHER'
                  ? postingContactData.user_responsible_name
                  : postingContactData.contact_name
            })}
            <LinkUpdate
              href={getLinkUpdateData(postingContactData.phone_source)}
              target='_blank'
              onClick={() =>
                sendCustomTrackingInfo({
                  event: 'select_content',
                  content_group: 'Panel',
                  content_type: 'Mis avisos',
                  item_id: 'Modificar datos de contacto'
                })
              }
            >
              {t(`panel.contactosPermisos.ContactData.${postingContactData.phone_source}.link`)}
            </LinkUpdate>
          </PContainer>
        </>
      )}
      {postingContactData.phone &&
        postingContactData.phone.map(contactData => {
          if (contactData.phone_type === 'WHATSAPP') {
            return (
              <PhoneContainer key={uuid()} data-panel='subtitletitle-phone-container'>
                <SubtitleContainer key={uuid()}>
                  {t(`panel.contactosPermisos.ContactData.InputDataContainer.WHATSAPP`)}
                </SubtitleContainer>
                <FeedbackContainer>
                  <InputContainter
                    key={uuid()}
                    data-panel='inputContainer-phone-WHATSAPP'
                    type={getInputStatusType(allPhonesAreValid(['WHATSAPP'], postingContactData.phone))}
                  >
                    <DataContainer key={uuid()}>
                      <DataSpan key={uuid()}>{contactData.phone}</DataSpan>
                    </DataContainer>
                  </InputContainter>
                  <FeddbackIcon type={getInputStatusType(allPhonesAreValid(['WHATSAPP'], postingContactData.phone))} />
                </FeedbackContainer>
                <WhatsAppHelp key={uuid()} hasError={!contactData.valid}>
                  {!contactData.valid && t('panel.contactosPermisos.ContactData.feedback.error')}
                  {t('panel.contactosPermisos.ContactData.InputDataContainer.WHATSAPP.help')}
                  <WhatsAppHelpStrong key={uuid()}>
                    {t('panel.contactosPermisos.ContactData.InputDataContainer.WHATSAPP.help.strong')}
                  </WhatsAppHelpStrong>
                </WhatsAppHelp>
              </PhoneContainer>
            );
          }
          return null;
        })}

      {postingContactData.phone_source === 'POSTING' && (
        <>
          <SubtitleContainer data-panel={`subtitletitle-email-${postingContactData.phone_source}`}>
            {t('panel.contactosPermisos.ContactData.InputDataContainer.email')}
          </SubtitleContainer>
          <InputContainter data-panel={`inputContainer-email-${postingContactData.phone_source}`}>
            <DataContainer>
              {postingContactData.emails.map(email => (
                <DataSpan key={uuid()}>{email}</DataSpan>
              ))}
            </DataContainer>
          </InputContainter>
        </>
      )}
    </>
  );
};

export default InputDataContainer;
